import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { Session } from "ezStore";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Checkbox, FormGroup, Input } from "@cofano/cck";
import { Box, Grid2 as Grid } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";
import { Fields, hasOptionalFieldPermission, usePermission } from "components/utils/Permission";

import ShipSelect from "view/ships/components/selects/ShipSelect";
import SailingScheduleSelect from "view/sailingschedules/components/selects/SailingScheduleSelect";
import { voyagePartialEditSchema, VoyagePartialEditSchemaInput, VoyagePartialEditSchemaOutput } from "view/voyages/utils/voyage.schema";

import { VoyageEditDialogCargoFragment, VoyageSummaryVoyageFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";
import { SaveVoyageDocument, UpdateReferencesDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

interface VoyageEditDialogProps {
	open: boolean;
	onClose: () => void;
	voyage: VoyageSummaryVoyageFragment;
	refetch: () => void;
	cargos?: VoyageEditDialogCargoFragment[] | null;
	session: Session;
}

const getDefaultReferencesValues = (voyage: VoyageSummaryVoyageFragment, cargos: VoyageEditDialogCargoFragment[]) => {
	// it's a bit weird, but we want to show the sales's charter number when filling in the purchase's customer reference.
	// to do this, we get all unique salescharters from the cargos, and then map from that salescharter to its purchasecharter, which is 1:1
	const filteredReferences = cargos?.map((cargo, index) => ({ charter: cargo.charter, index: index }))
		.filter((value, index, self) => index === self.findIndex(x => x?.charter?.id === value?.charter?.id))
		.map(x => ({
			index: x.index,
			sales: x.charter,
			purchase: x.charter?.purchaseCharter,
		})) ?? [];

	const references: { id: number, charterNumber: string, customerReference: string }[] = [];

	filteredReferences.forEach((x) => {
		references[x.index] = {
			id: x.purchase?.id ?? 0,
			customerReference: x.purchase?.customerReference ?? "",
			charterNumber: x.sales?.charterNumber ?? "",
		};
	});

	return references;
};

const VoyageEditDialog = ({ open, voyage, cargos, onClose, refetch, session }: VoyageEditDialogProps) => {
	const { isRTR } = usePermission();

	const { control, handleSubmit, watch } = useForm<VoyagePartialEditSchemaInput, unknown, VoyagePartialEditSchemaOutput>({
		resolver: zodResolver(voyagePartialEditSchema),
		defaultValues: {
			id: voyage.id,
			voyageNumber: voyage.voyageNumber,
			ship: voyage.ship,
			schedule: voyage.schedule ?? null,
			isBallast: voyage.isBallast ?? false,
			references: getDefaultReferencesValues(voyage, cargos ?? []),
		},
	});

	const [ saveVoyage, { loading } ] = useMutation(SaveVoyageDocument);
	const [ saveReference, { loading: savingReference } ] = useMutation(UpdateReferencesDocument);
	const { references } = watch();

	const save = async (data: VoyagePartialEditSchemaOutput) => {
		if (isRTR) {
			await saveVoyage({
				variables: {
					input: {
						id: data.id,
						voyageNumber: data.voyageNumber,
						shipId: data.ship.id,
						schedule: data.schedule ?? null,
						isBallast: data.isBallast,
					},
					partialUpdate: true,
				},
			});
		}

		if (hasOptionalFieldPermission(session, Fields.VOYAGE, "reference")) {
			await saveReference({
				variables: {
					charterReferences: data.references?.map(x => ({
						charterId: x.id,
						reference: x.customerReference,
					})) ?? [],
				},
			});
		}

		refetch();
		onClose();
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			loading={loading || savingReference}
			form="voyage-edit-form"
			title={intl.formatMessage({ id: "EDIT_VOYAGE" })}
		>
			<form id="voyage-edit-form" onSubmit={handleSubmit(save)}>
				<Grid container columnSpacing={3} rowSpacing={2}>
					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "SHIP" })} required>
							<ShipSelect value={voyage.ship} disabled />
						</FormGroup>
					</Grid>

					<Grid size={6}>
						<FormGroup label={intl.formatMessage({ id: "VOYAGE_NUMBER" })} required>
							<Input value={voyage.voyageNumber} disabled />
						</FormGroup>
					</Grid>

					{hasOptionalFieldPermission(session, Fields.VOYAGE, "schedule") && (
						<Grid size={6}>
							<FormGroup label={intl.formatMessage({ id: "VOYAGE_SCHEDULE" })}>
								<Controller
									name="schedule"
									control={control}
									render={({ field }) => (
										<SailingScheduleSelect
											value={field.value}
											disabled={!isRTR}
											onChange={field.onChange}
										/>
									)}
								/>
							</FormGroup>
						</Grid>
					)}

					{hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast") && (
						<Grid size={6}>
							<FormGroup label={intl.formatMessage({ id: "IS_BALLAST" })}>
								<Box marginTop={1}>
									<Controller
										name="isBallast"
										control={control}
										render={({ field }) => (
											<Checkbox
												value={undefined}
												disabled={!isRTR}
												checked={field.value ?? undefined}
												onChange={field.onChange}
											/>
										)}
									/>
								</Box>
							</FormGroup>
						</Grid>
					)}

					{hasOptionalFieldPermission(session, Fields.VOYAGE, "reference") && (
						references?.map((reference, index) =>
							<Grid size={6} key={reference.id}>
								<FormGroup label={`${intl.formatMessage({ id: "REFERENCE" })} (${reference.charterNumber})`}>
									<Controller
										name={`references.${index}.customerReference`}
										control={control}
										render={({ field }) => <Input {...field} />}
									/>
								</FormGroup>
							</Grid>,
						)
					)}
				</Grid>
			</form>
		</FormDialog>
	);
};

export default VoyageEditDialog;
